import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import SchemaMarkup from '../components/SchemaMarkup';
import { pageSchemas } from '../data/pageSchemas';
import { Search, CheckCircle, Clock, AlertCircle, DollarSign, XCircle, Copy, MessageCircle, TrendingUp } from 'lucide-react';

const TrackSubmission = () => {
  const [referenceNumber, setReferenceNumber] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [error, setError] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [copied, setCopied] = useState(false);

  // Update current time every minute for real-time elapsed time display
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute
    
    return () => clearInterval(timer);
  }, []);

  const handleTrack = async (e) => {
    e.preventDefault();
    setError('');
    setSubmission(null);
    setLoading(true);

    try {
      // Clean up inputs: remove # from reference, trim whitespace
      const cleanRefNumber = referenceNumber.trim().replace('#', '').toUpperCase();
      const cleanEmail = email.trim().toLowerCase();
      
      const backendUrl = window.location.hostname === 'localhost' 
        ? (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001')
        : '';  // Empty string means relative URL - same domain as frontend
      const response = await fetch(
        `${backendUrl}/api/track-submission?reference_number=${encodeURIComponent(cleanRefNumber)}&email=${encodeURIComponent(cleanEmail)}`
      );
      
      const data = await response.json();
      
      if (data.success) {
        setSubmission(data.submission);
      } else {
        setError(data.message || 'Unable to find submission');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getElapsedTime = (submittedAt) => {
    if (!submittedAt) return { hours: 0, minutes: 0, display: 'Just now' };
    
    const submitted = new Date(submittedAt);
    const diff = currentTime - submitted;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours === 0 && minutes === 0) return { hours: 0, minutes: 0, display: 'Just now' };
    if (hours === 0) return { hours: 0, minutes, display: `${minutes} minute${minutes !== 1 ? 's' : ''} ago` };
    if (hours < 24) return { hours, minutes, display: `${hours}h ${minutes}m ago` };
    
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return { 
      hours, 
      minutes, 
      display: `${days} day${days !== 1 ? 's' : ''} ${remainingHours}h ago` 
    };
  };

  const getSmartMessage = (status, elapsedHours, submittedAt) => {
    const now = new Date();
    const hour = now.getHours();
    const day = now.getDay();
    const isBusinessHours = (day >= 1 && day <= 5) && (hour >= 9 && hour <= 18); // Mon-Fri 9AM-6PM
    const isWeekend = day === 0 || day === 6;

    if (status === 'received') {
      if (elapsedHours < 2) {
        return {
          mood: 'positive',
          title: '✅ Received & Queued',
          message: 'Your submission has been received and is waiting for our review team.',
          expectation: isBusinessHours ? 'Typical wait: 1-3 hours during business hours' : 'Review will begin during business hours (Mon-Fri, 9 AM - 6 PM EST)',
          nextStep: "You'll receive an email when review starts",
          color: 'blue',
          traffic: '🟢'
        };
      } else if (elapsedHours < 6) {
        return {
          mood: 'neutral',
          title: '⏳ Waiting for Review',
          message: 'Your submission is in queue. During peak times, wait may be slightly longer.',
          expectation: 'Most submissions are picked up within 4-6 hours',
          nextStep: 'Review will begin soon',
          color: 'blue',
          traffic: '🟡'
        };
      } else {
        return {
          mood: 'caution',
          title: '🔔 Extended Queue Time',
          message: isWeekend ? 'Submitted during weekend - review will begin on next business day' : 'Higher than usual queue time. Your submission is logged and will be reviewed.',
          expectation: 'Review typically starts within 12 hours',
          nextStep: 'You can contact support if urgent',
          color: 'yellow',
          traffic: '🟡',
          showSupport: true
        };
      }
    }

    if (status === 'under_review') {
      if (elapsedHours < 4) {
        return {
          mood: 'positive',
          title: '🔍 Under Active Review',
          message: 'Our team is carefully verifying your gift card details and market rates.',
          expectation: 'Most reviews complete within 2-8 hours',
          nextStep: 'Status will update automatically',
          color: 'yellow',
          traffic: '🟢'
        };
      } else if (elapsedHours < 12) {
        return {
          mood: 'neutral',
          title: '⏳ Review in Progress',
          message: 'Your submission requires additional verification. This is normal for certain card types or values.',
          expectation: 'Some cards take 6-12 hours for thorough review',
          nextStep: 'Updates provided every few hours',
          color: 'yellow',
          traffic: '🟡'
        };
      } else if (elapsedHours < 24) {
        return {
          mood: 'caution',
          title: '⚠️ Extended Verification',
          message: 'Extended review is needed. Common reasons: high-value cards ($500+), rare brands, or market rate verification.',
          expectation: 'Complex cases may take up to 24 hours',
          nextStep: 'Our senior team is handling this',
          color: 'orange',
          traffic: '🟡'
        };
      } else {
        return {
          mood: 'urgent',
          title: '🔔 Manager Review Required',
          message: 'Your submission requires senior team review. This happens with special cases that need extra attention.',
          expectation: 'Resolution expected within 48 hours of submission',
          nextStep: 'Need urgent update? Contact our support team',
          color: 'orange',
          traffic: '🔴',
          showSupport: true
        };
      }
    }

    if (status === 'approved') {
      return {
        mood: 'success',
        title: '✅ Approved - Payment Processing',
        message: 'Great news! Your gift card has been verified and approved.',
        expectation: 'Payment typically sent within 1-2 hours',
        nextStep: "You'll receive confirmation email once payment is sent",
        color: 'green',
        traffic: '🟢'
      };
    }

    if (status === 'paid') {
      return {
        mood: 'success',
        title: '💰 Payment Completed',
        message: 'Payment has been successfully sent to your provided payment method.',
        expectation: 'Allow 1-3 business days for funds to reflect in your account',
        nextStep: 'Thank you for choosing CashifyGCMart!',
        color: 'green',
        traffic: '🟢'
      };
    }

    if (status === 'rejected') {
      return {
        mood: 'error',
        title: '❌ Submission Declined',
        message: 'Unfortunately, we cannot proceed with this submission.',
        expectation: 'Please check admin message below for details',
        nextStep: 'Contact support for clarification or to submit a different card',
        color: 'red',
        traffic: '🔴',
        showSupport: true
      };
    }

    return {
      mood: 'neutral',
      title: 'Status Unknown',
      message: 'Unable to determine current status',
      expectation: '',
      nextStep: 'Please contact support',
      color: 'gray',
      traffic: '⚪',
      showSupport: true
    };
  };

  const getProgressStages = (currentStatus) => {
    const stages = [
      { key: 'received', label: 'Received', icon: <CheckCircle className="w-5 h-5" /> },
      { key: 'under_review', label: 'Under Review', icon: <Clock className="w-5 h-5" /> },
      { key: 'approved', label: 'Approved', icon: <CheckCircle className="w-5 h-5" /> },
      { key: 'paid', label: 'Paid', icon: <DollarSign className="w-5 h-5" /> }
    ];

    const statusOrder = { 'received': 0, 'under_review': 1, 'approved': 2, 'paid': 3, 'rejected': -1 };
    const currentIndex = statusOrder[currentStatus] || 0;

    return stages.map((stage, index) => ({
      ...stage,
      isCompleted: index < currentIndex,
      isCurrent: index === currentIndex,
      isPending: index > currentIndex
    }));
  };

  const getTotalValue = (cards) => {
    if (!cards || cards.length === 0) return 0;
    return cards.reduce((sum, card) => sum + parseFloat(card.value || 0), 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        timeZone: 'America/New_York', // US Eastern Time
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }) + ' ET';
    } catch {
      return dateString;
    }
  };

  return (
    <>
      <Helmet>
        <title>Track Your Submission | CashifyGCMart</title>
        <meta name="description" content="Track the status of your gift card submission in real time. Enter your reference number and email to see verification, approval, and payout progress." />
        <link rel="canonical" href="https://cashifygcmart.com/track-submission" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Track Your Submission | CashifyGCMart" />
        <meta property="og:description" content="Track the status of your gift card submission in real time." />
        <meta property="og:url" content="https://cashifygcmart.com/track-submission" />
      </Helmet>
      <SchemaMarkup faqs={pageSchemas.trackSubmission.faqs} />

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Track Your Submission
            </h1>
            <p className="text-lg text-gray-600">
              Enter your reference number and email to check the status
            </p>
          </div>

          {/* Tracking Form */}
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-6">
            <form onSubmit={handleTrack} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Reference Number
                </label>
                <input
                  type="text"
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                  placeholder="GC-112802-83"
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none transition-all"
                />
                <p className="text-xs text-gray-500 mt-1">
                  📧 Found in your confirmation email (with or without #)
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none transition-all"
                />
                <p className="text-xs text-gray-500 mt-1">
                  The email used during submission
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold py-4 rounded-lg hover:from-pink-600 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Tracking...
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5" />
                    Track Submission
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Submission Details */}
          {submission && (
            <div className="space-y-6">
              {/* Smart Status Card with Traffic Light */}
              <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 border-2 border-gray-200">
                {(() => {
                  const elapsed = getElapsedTime(submission.submitted_at);
                  const smartMsg = getSmartMessage(submission.status, elapsed.hours, submission.submitted_at);
                  
                  return (
                    <>
                      {/* Traffic Light & Title */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <span className="text-4xl">{smartMsg.traffic}</span>
                          <div>
                            <h2 className="text-2xl font-bold text-gray-900">
                              {smartMsg.title}
                            </h2>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-sm text-gray-500">Ref: {submission.reference_number}</span>
                              <button
                                onClick={() => copyToClipboard(submission.reference_number)}
                                className="text-pink-600 hover:text-pink-700 transition-colors"
                                title="Copy reference number"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                              {copied && <span className="text-xs text-green-600 font-medium">Copied!</span>}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Elapsed Time Display */}
                      <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600 font-medium">⏱️ Time Elapsed</p>
                            <p className="text-xl font-bold text-gray-900">{elapsed.display}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-600 font-medium">📊 Typical Range</p>
                            <p className="text-lg font-bold text-gray-700">{smartMsg.expectation}</p>
                          </div>
                        </div>
                      </div>

                      {/* Smart Message */}
                      <div className={`bg-${smartMsg.color}-50 border border-${smartMsg.color}-200 rounded-lg p-4 mb-4`}>
                        <p className="text-base text-gray-800 font-medium mb-2">{smartMsg.message}</p>
                        <p className="text-sm text-gray-700">💡 {smartMsg.nextStep}</p>
                      </div>

                      {/* Contact Support Button */}
                      {smartMsg.showSupport && (
                        <a
                          href="mailto:support@cashifygcmart.com"
                          className="inline-flex items-center gap-2 bg-pink-600 text-white px-4 py-2 rounded-lg hover:bg-pink-700 transition-colors font-medium"
                        >
                          <MessageCircle className="w-4 h-4" />
                          Contact Support
                        </a>
                      )}

                      {/* Admin Message to Customer */}
                      {submission.customer_message && (
                        <div className="mt-4 pt-4 border-t-2 border-gray-200">
                          <p className="text-sm text-gray-600 font-semibold mb-2">💬 Message from Support:</p>
                          <p className="text-base text-gray-900 bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                            {submission.customer_message}
                          </p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Visual Progress Timeline */}
              <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Progress Timeline</h3>
                <div className="relative">
                  {getProgressStages(submission.status).map((stage, index, array) => (
                    <div key={stage.key} className="flex items-start mb-6 last:mb-0">
                      {/* Icon Circle */}
                      <div className="relative flex-shrink-0">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          stage.isCompleted ? 'bg-green-500 text-white' :
                          stage.isCurrent ? 'bg-blue-500 text-white animate-pulse' :
                          'bg-gray-300 text-gray-500'
                        }`}>
                          {stage.icon}
                        </div>
                        {/* Connecting Line */}
                        {index < array.length - 1 && (
                          <div className={`absolute left-5 top-10 w-0.5 h-12 ${
                            stage.isCompleted ? 'bg-green-500' : 'bg-gray-300'
                          }`} />
                        )}
                      </div>
                      
                      {/* Stage Info */}
                      <div className="ml-4 flex-grow">
                        <p className={`font-semibold ${
                          stage.isCompleted ? 'text-green-700' :
                          stage.isCurrent ? 'text-blue-700' :
                          'text-gray-500'
                        }`}>
                          {stage.label}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          {stage.isCompleted && '✓ Completed'}
                          {stage.isCurrent && '🔄 In Progress'}
                          {stage.isPending && '⏳ Pending'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Card Details with Total Value */}
              <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Gift Card Details</h3>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Total Value</p>
                    <p className="text-2xl font-bold text-pink-600">${getTotalValue(submission.cards).toFixed(2)}</p>
                  </div>
                </div>
                
                <div className="space-y-3 mb-6">
                  {submission.cards && submission.cards.map((card, index) => (
                    <div key={index} className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg p-4 border-2 border-pink-200">
                      <div className="flex justify-between items-start">
                        <div className="flex-grow">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="bg-pink-600 text-white text-xs font-bold px-2 py-1 rounded">
                              Card {index + 1}
                            </span>
                            <p className="font-bold text-gray-900 text-lg">{card.brand}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-gray-600">Condition:</span>
                              <span className="ml-2 font-medium text-gray-900 capitalize">{card.condition}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Type:</span>
                              <span className="ml-2 font-medium text-gray-900 capitalize">{card.cardType}</span>
                            </div>
                            {card.hasReceipt && (
                              <div>
                                <span className="text-gray-600">Receipt:</span>
                                <span className="ml-2 font-medium text-green-600">✓ Yes</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <p className="text-2xl font-bold text-pink-600">${parseFloat(card.value).toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Submission Info */}
                <div className="border-t pt-4 space-y-3">
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600 font-medium">Customer Name</span>
                    <span className="text-gray-900 font-semibold">{submission.customer_name}</span>
                  </div>
                  
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600 font-medium">Submitted On</span>
                    <span className="text-gray-900 font-semibold">{formatDate(submission.submitted_at)}</span>
                  </div>
                  
                  {submission.status_updated_at && (
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600 font-medium">Last Updated</span>
                      <span className="text-gray-900 font-semibold">{formatDate(submission.status_updated_at)}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600 font-medium">Payment Method</span>
                    <div className="text-right">
                      <div className="text-gray-900 font-semibold uppercase">{submission.paymentMethod}</div>
                      {submission.paymentMethod && (
                        <>
                          {submission.paymentMethod.toLowerCase() === 'paypal' && submission.paypalAddress && (
                            <div className="text-sm text-gray-600 mt-1">{submission.paypalAddress}</div>
                          )}
                          {(submission.paymentMethod.toLowerCase() === 'cashapp' || submission.paymentMethod.toLowerCase() === 'cash app') && submission.cashAppTag && (
                            <div className="text-sm text-gray-600 mt-1">{submission.cashAppTag}</div>
                          )}
                          {submission.paymentMethod.toLowerCase() === 'zelle' && submission.zelleDetails && (
                            <div className="text-sm text-gray-600 mt-1">{submission.zelleDetails}</div>
                          )}
                          {(submission.paymentMethod.toLowerCase() === 'bitcoin' || submission.paymentMethod.toLowerCase() === 'btc') && submission.btcAddress && (
                            <div className="text-sm text-gray-600 mt-1 break-all">{submission.btcAddress}</div>
                          )}
                          {submission.paymentMethod.toLowerCase() === 'chime' && submission.chimeDetails && (
                            <div className="text-sm text-gray-600 mt-1">{submission.chimeDetails}</div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* What Happens Next - Context-aware */}
              {submission.status !== 'paid' && submission.status !== 'rejected' && (
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-2xl p-6">
                  <h4 className="text-lg font-bold text-blue-900 mb-3 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    What Happens Next?
                  </h4>
                  <div className="space-y-2 text-blue-900">
                    {submission.status === 'received' && (
                      <>
                        <p className="flex items-start gap-2">
                          <span className="font-bold">1.</span>
                          <span>Our team will pick up your submission from the queue</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <span className="font-bold">2.</span>
                          <span>We'll verify card details and check current market rates</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <span className="font-bold">3.</span>
                          <span>Once approved, payment will be processed immediately</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <span className="font-bold">4.</span>
                          <span>You'll receive confirmation email with payment details</span>
                        </p>
                      </>
                    )}
                    {submission.status === 'under_review' && (
                      <>
                        <p className="flex items-start gap-2">
                          <span className="font-bold">1.</span>
                          <span>Team is currently verifying your card authenticity and value</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <span className="font-bold">2.</span>
                          <span>Checking current market rates for best payout</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <span className="font-bold">3.</span>
                          <span>Once verified, status will change to "Approved"</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <span className="font-bold">4.</span>
                          <span>Payment processing begins immediately after approval</span>
                        </p>
                      </>
                    )}
                    {submission.status === 'approved' && (
                      <>
                        <p className="flex items-start gap-2">
                          <span className="font-bold">1.</span>
                          <span>Your card is approved! Payment is being processed right now</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <span className="font-bold">2.</span>
                          <span>Funds will be sent to your {submission.paymentMethod} account
                            {submission.paymentMethod && (
                              <>
                                {submission.paymentMethod.toLowerCase() === 'paypal' && submission.paypalAddress && ` (${submission.paypalAddress})`}
                                {(submission.paymentMethod.toLowerCase() === 'cashapp' || submission.paymentMethod.toLowerCase() === 'cash app') && submission.cashAppTag && ` (${submission.cashAppTag})`}
                                {submission.paymentMethod.toLowerCase() === 'zelle' && submission.zelleDetails && ` (${submission.zelleDetails})`}
                                {submission.paymentMethod.toLowerCase() === 'chime' && submission.chimeDetails && ` (${submission.chimeDetails})`}
                              </>
                            )}
                          </span>
                        </p>
                        <p className="flex items-start gap-2">
                          <span className="font-bold">3.</span>
                          <span>You'll receive payment confirmation email shortly</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <span className="font-bold">4.</span>
                          <span>Please allow 1-3 business days for funds to appear</span>
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Success Message for Paid Status */}
              {submission.status === 'paid' && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-2xl p-6 text-center">
                  <div className="text-6xl mb-4">🎉</div>
                  <h4 className="text-2xl font-bold text-green-900 mb-2">Payment Completed!</h4>
                  <p className="text-green-800 text-lg">
                    Thank you for choosing CashifyGCMart. We hope to serve you again!
                  </p>
                </div>
              )}
            </div>
          )}

          {/* SEO content — explains the tracking process to visitors and search engines */}
          <section className="mt-16 bg-white rounded-2xl shadow-sm p-8 sm:p-10 prose max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How gift card tracking works at CashifyGCMart</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              After you submit a gift card through our secure form, every submission is assigned
              a unique reference number that travels with your card through verification, approval,
              and payout. This tracker lets you check exactly where your card stands in real time —
              no email back-and-forth, no waiting on customer support to respond.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">What each status means</h3>
            <ul className="space-y-3 text-gray-700">
              <li>
                <strong>Pending</strong> — your submission has been received and queued for
                verification. This usually lasts a few minutes to a few hours depending on volume.
              </li>
              <li>
                <strong>Verifying</strong> — one of our specialists is actively checking the card
                balance with the retailer. Physical cards may take a little longer than digital
                codes because we sometimes need extra clarification.
              </li>
              <li>
                <strong>Approved</strong> — the card was successfully verified and your final
                payout amount is confirmed. Payment is queued to your chosen method.
              </li>
              <li>
                <strong>Paid</strong> — the money has been sent to your PayPal, Zelle, CashApp,
                or bank account. You should see the funds shortly after this status flips.
              </li>
              <li>
                <strong>Rejected</strong> — the card could not be verified (already redeemed,
                incorrect balance, or fraud indicators). We always email you the reason.
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">How long does the process take?</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Most submissions move from <em>pending</em> to <em>paid</em> in 6–14 hours during
              business days. Digital cards from major brands (Amazon, Apple, Walmart, Target)
              usually finish in under 8 hours because they can be verified instantly. Physical
              cards and less common brands may take up to 24 hours if the retailer's balance
              lookup is slow.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Can't find your reference number?</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Your reference number is included in the confirmation email we sent right after
              you submitted the card. Search your inbox for "CashifyGCMart" and it will show
              up as a bold string near the top of the email. Both the reference number and the
              email you used to submit are required to look up your status — this two-factor
              lookup keeps your submission private.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Need to reach a human?</h3>
            <p className="text-gray-700 leading-relaxed">
              If your submission has been sitting in the same status for more than 24 hours,
              use the live chat in the corner of any page or reply to the confirmation email.
              A real specialist — not a bot — will get back to you the same business day.
            </p>
          </section>
        </div>
      </div>
    </>
  );
};

export default TrackSubmission;
